import {
    redirect,
    ActionFunctionArgs,
    Form,
    useLoaderData,
} from "react-router-dom";
import toast from "react-hot-toast";
import IndeterminateCheckbox from "../../components/inputs/IndeterminantCheckbox";
import {Input} from "../../components/inputs/Input";
import CustomSelect from "../../components/customSelect";
import {FormLayout} from "../../layout/FormLayout";
import {createApolloClient} from "../../providers/ApolloClientFactory";
import {useOrganizationStore} from "../../store/organization";
import {CreatePaymentProcessor} from "./mutations";
import {useState} from "react";
import PercentageInput from "../../components/inputs/PercentageInput";
import {userStore} from "../../store/user";
import {organizationStore} from "../../store/organization";
import {PAYMENT_PROCESSOR_CATAGORIES} from "./queries";
import {Categories} from "../../types/item";
import PriceInput from "../../components/inputs/PriceInput";

const client = createApolloClient();
const {getState: getUserStore} = userStore;
const {getState: getOrganizationStore} = organizationStore;
export const paymentProcessorsLoader = async () => {
    const organizationId = getOrganizationStore().organizationId;
    const {data: paymentTypeData} = await client.query({
        query: PAYMENT_PROCESSOR_CATAGORIES,
        variables: {
            where: organizationId
                ? {
                    organization_id: {
                        _in: [0, organizationId],
                    },
                    is_active: {
                        _eq: true,
                    },
                    reference_type: {
                        _eq: "payment_processors_processor",
                    },
                }
                : {},
        },
    });
    return {
        paymentType: paymentTypeData.dashboard_reference,
    };
};

export const paymentProcessorCreateAction = async ({
                                                       request,
                                                   }: ActionFunctionArgs) => {
    const user = getUserStore().user;

    const formData = await request.formData();
    const body = Object.fromEntries(formData.entries());
    const priceString = formData.get("application_fee_per_transaction") as string;
    // Blank fee field falls back to the platform default of $0.19 — an explicit
    // "0" typed by ops is respected (parses to 0), only an EMPTY field defaults.
    const parsedPrice = parseFloat(priceString);
    const price = Number.isFinite(parsedPrice) ? parsedPrice : 0.19;
    // RP-2552: a Stripe processor needs its Terminal location, or card readers
    // at the venue can't connect. Every saved location id starts with "tml_",
    // so anything else is a paste into the wrong box (often the acct_ id).
    const locationId = String(body.platform_account_location_id || "").trim();
    if (body.processor === "stripe") {
        if (!locationId) {
            toast.error("Connected Account Location ID is required");
            return null;
        }
        if (!locationId.startsWith("tml_")) {
            toast.error('Connected Account Location ID should start with "tml_"');
            return null;
        }
    }
    try {
        const processingRate = (body.processing_rate || "0") as string;
        const processingRatePer = (parseFloat(processingRate) / 100).toFixed(8);
        const config =
            body.processor === "stripe"
                ? {
                    connected_account_id: String(body.connected_account_id || "").trim(),
                    platform_account_location_id: locationId,
                    currency: body.currency || null,
                    deal_structure: body.deal_structure,
                    processing_rate: processingRatePer,
                    application_fee_per_transaction: price
                }
                : {
                    secretKey: body.secretKey,
                    merchantId: body.merchantId,
                    consumerKey: body.consumerKey,
                    merchantAccountId: body.merchantAccountId || null,
                };
        await client.mutate({
            mutation: CreatePaymentProcessor,
            variables: {
                input: {
                    payment_processor: body.payment_processor,
                    processor: body.processor,
                    organization_id: body.organization_id || null,
                    config,
                    is_active: body.is_active === "true",
                    processing_rate: processingRatePer,
                    last_updated_by: user?.id,
                },
            },
        });
        toast.success("Payment processor created");
        return redirect("/payment-processors/list");
    } catch {
        toast.error("Unable to create payment processor");
        return null;
    }
};

export const PaymentProcessorCreate = () => {
    const organizationId = useOrganizationStore((state) => state.organizationId);
    const organizations = useOrganizationStore((state) => state.organizations);
    const [processor, setProcessor] = useState("");
    const {paymentType} = useLoaderData() as {
        paymentType: Categories[];
    };
    // Defaults for new Stripe processors: $0.19 / transaction + 2.9% processing
    // rate. Ops can override before saving; this just removes the busywork of
    // typing the same numbers for every new merchant.
    const [price, setPrice] = useState<number>(0.19);
    const handlePriceChange = (value: number) => {
        setPrice(value);
    };

    return (
        <Form method="post" action="/payment-processors/create">
            <FormLayout>
                <div className="col-span-4">
                    <CustomSelect
                        title="Organization"
                        name="organization_id"
                        required
                        options={[
                            ...organizations.map((org) => ({
                                value: org.id,
                                label: org.name,
                            })),
                        ]}
                    />
                </div>
                <div className="col-span-1">
                    <IndeterminateCheckbox
                        label="Is Active?"
                        name="is_active"
                        defaultChecked={true}
                    />
                </div>
                <div className="col-span-6">
                    <CustomSelect
                        title="Processor"
                        required
                        name="processor"
                        onChange={(value) => setProcessor(value as string)}
                        options={[
                            ...(paymentType?.map((v) => ({
                                label: v.reference_label,
                                value: v.reference_value,
                            })) || []),
                        ]}
                    />
                </div>
                {processor === "mxmerchant" && (
                    <>
                        <div className="col-span-6">
                            <Input
                                label="Payment Processor Name"
                                name="payment_processor"
                                required
                            />
                        </div>
                        <div className="col-span-6">
                            <Input label="Consumer Key" name="consumerKey" required/>
                        </div>
                        <div className="col-span-6">
                            <Input label="Secret Key" name="secretKey" required/>
                        </div>
                        <div className="col-span-6">
                            <Input label="Merchant ID" name="merchantId" required/>
                        </div>
                        <div className="col-span-6">
                            <PercentageInput
                                defaultValue={0}
                                label="Processing Rate Percentage ( 7.5 = 7.5% )"
                                name="processing_rate"
                            />
                        </div>
                    </>
                )}
                {processor === "braintree" && (
                    <>
                        <div className="col-span-6">
                            <Input
                                label="Payment Processor Name"
                                name="payment_processor"
                                required
                            />
                        </div>
                        <div className="col-span-6">
                            <Input label="Public Key" name="consumerKey" required/>
                        </div>
                        <div className="col-span-6">
                            <Input label="Private Key" name="secretKey" required/>
                        </div>
                        <div className="col-span-6">
                            <Input label="Merchant ID" name="merchantId" required/>
                        </div>
                        <div className="col-span-6">
                            <Input
                                label="Merchant Account ID"
                                name="merchantAccountId"
                                required
                            />
                        </div>
                        <div className="col-span-6">
                            <PercentageInput
                                defaultValue={0}
                                label="Processing Rate Percentage ( 7.5 = 7.5% )"
                                name="processing_rate"
                            />
                        </div>
                    </>
                )}
                {processor === "stripe" && (
                    <>
                        <div className="col-span-6">
                            <Input
                                label="Payment Processor Name"
                                name="payment_processor"
                                required
                            />
                        </div>
                        <div className="col-span-6">
                            <Input
                                label="Connected Account ID"
                                name="connected_account_id"
                                required
                            />
                        </div>
                        <div className="col-span-6">
                            <Input
                                label="Connected Account Location ID"
                                name="platform_account_location_id"
                                placeholder="tml_..."
                                required
                            />
                        </div>
                        <div className="col-span-6">
                        <CustomSelect
                            title="Currency"
                            name="currency"
                            required
                            options={[
                                { value: "usd", label: "USD" },
                                { value: "cad", label: "CAD" },
                            ]}
                        />
                        </div>
                        <div className="col-span-6">
                            <CustomSelect
                                title="Deal Structure"
                                name="deal_structure"
                                required
                                defaultValue="percentOnTop"
                                options={[
                                    { value: "percentOnTop", label: "Percent On Top" },
                                    { value: "inclusiveOfCustomerCharge", label: "Inclusive of Customer Charge" },
                                ]}
                            />
                        </div>
                        <div className="col-span-6">
                            <PercentageInput
                                defaultValue={2.9}
                                label="Processing Rate Percentage ( 7.5 = 7.5% )"
                                name="processing_rate"
                            />
                        </div>
                        <div className="col-span-6">
                        <PriceInput
                            label="Fee Per Transaction"
                            name="application_fee_per_transaction"
                            onChange={(value) => handlePriceChange(value ? value : 0)}
                            value={price}
                        />
                        </div>
                    </>
                )}
            </FormLayout>
        </Form>
    );
};
